import React from "react";
import { Box } from "@mui/material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../utils/dt";

interface DecommissionViewToggleProps {
  activeView: "active" | "decommissioned";
  activeCount: number;
  decommissionedCount: number;
  onViewChange: (view: "active" | "decommissioned") => void;
}

const DecommissionViewToggle = ({
  activeView,
  activeCount,
  decommissionedCount,
  onViewChange,
}: DecommissionViewToggleProps) => {
  const { translate } = useTranslation("myMachinery");

  const pillBase = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: 500,
    fontFamily: dt.font,
    cursor: "pointer",
    transition: "all 0.2s ease",
    userSelect: "none" as const,
    whiteSpace: "nowrap" as const,
    lineHeight: "20px",
  };

  const countBadge = (count: number, isSelected: boolean, color: string) => (
    <Box
      component="span"
      sx={{
        fontSize: "11px",
        fontFamily: dt.fontMono,
        fontWeight: 700,
        backgroundColor: isSelected ? `${color}20` : "#F3F4F6",
        color: isSelected ? color : dt.textMuted,
        borderRadius: "10px",
        padding: "1px 7px",
        lineHeight: "16px",
        transition: "all 0.2s ease",
      }}
    >
      {count}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: "#F3F4F6",
        borderRadius: "22px",
        padding: "3px",
        gap: "2px",
      }}
    >
      {/* Active pill */}
      <Box
        onClick={() => onViewChange("active")}
        sx={{
          ...pillBase,
          backgroundColor: activeView === "active" ? "#FFFFFF" : "transparent",
          color: activeView === "active" ? dt.blue : dt.textSecondary,
          boxShadow: activeView === "active" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
          "&:hover": {
            backgroundColor: activeView === "active" ? "#FFFFFF" : "#E5E7EB",
          },
        }}
      >
        {translate("activeView")}
        {countBadge(activeCount, activeView === "active", dt.blue)}
      </Box>

      {/* Decommissioned pill */}
      <Box
        onClick={() => onViewChange("decommissioned")}
        sx={{
          ...pillBase,
          backgroundColor: activeView === "decommissioned" ? "#FFFFFF" : "transparent",
          color: activeView === "decommissioned" ? dt.decommissioned.amber : dt.textSecondary,
          boxShadow: activeView === "decommissioned" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
          "&:hover": {
            backgroundColor: activeView === "decommissioned" ? "#FFFFFF" : "#E5E7EB",
          },
        }}
      >
        {translate("decommissionedView")}
        {countBadge(decommissionedCount, activeView === "decommissioned", dt.decommissioned.amber)}
      </Box>
    </Box>
  );
};

export default DecommissionViewToggle;
